import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { hasPassiveSupport } from '@polestar/component-warehouse-react/helpers/supports';
import styled from '@emotion/styled';
import { useIsSSR } from '@polestar/component-warehouse-react/contexts/ssr';
import useMutableValue from '@polestar/component-warehouse-react/hooks/useMutableValue';
import useInView from '@polestar/component-warehouse-react/hooks/useInView';
const InViewContext = createContext({
    threshold: 0,
});
const InView = ({ onEnter, onLeave, placeholder, children, scrollRef, offset = '0px 0px 0px 0px', threshold = 0, alwaysRenderChildren = false, triggerOnce = false, }) => {
    const [observableRef, isIntersected] = useInView({
        motionValue: true,
        triggerOnce,
        root: scrollRef === null || scrollRef === void 0 ? void 0 : scrollRef.current,
        rootMargin: offset,
        threshold,
    });
    const [shouldRender, setShouldRender] = useState(alwaysRenderChildren);
    const isInView = useMutableValue(false);
    const isSSR = useIsSSR();
    const topRef = useRef(null);
    const bottomRef = useRef(null);
    useEffect(() => {
        const calculateDifference = () => {
            if (observableRef.current && bottomRef.current && topRef.current) {
                observableRef.current.style.height = `${bottomRef.current.offsetTop - topRef.current.offsetTop}px`;
            }
        };
        calculateDifference();
        window.addEventListener('resize', calculateDifference, hasPassiveSupport ? { passive: true } : false);
        return () => {
            window.removeEventListener('resize', calculateDifference);
        };
    }, [observableRef, shouldRender]);
    useEffect(() => isIntersected.on('change', (latestIntersected) => {
        isInView.set(latestIntersected);
        if (latestIntersected) {
            setShouldRender(true);
            onEnter === null || onEnter === void 0 ? void 0 : onEnter();
        }
        else {
            onLeave === null || onLeave === void 0 ? void 0 : onLeave();
        }
    }), [isIntersected, onEnter, onLeave, isInView]);
    return (React.createElement(InViewContext.Provider, { value: {
            threshold,
            inView: isInView,
        } },
        React.createElement(Positioner, { ref: topRef },
            React.createElement("div", { ref: observableRef, style: { width: 1, opacity: 0 } })),
        shouldRender ? children : !isSSR && (placeholder !== null && placeholder !== void 0 ? placeholder : null),
        isSSR && React.createElement("noscript", null, children),
        React.createElement(Positioner, { ref: bottomRef })));
};
InView.displayName = 'InView';
export default InView;
// TODO: add visual screenshot tests to validate this functionality
export const useLoadMediaInView = ({ onInView, onChange }) => {
    const initialRender = useRef(false);
    const { inView } = useContext(InViewContext);
    useEffect(() => {
        return inView === null || inView === void 0 ? void 0 : inView.on('change', (value) => {
            if (value === true && !initialRender.current) {
                onInView === null || onInView === void 0 ? void 0 : onInView();
                initialRender.current = true;
            }
            if (initialRender.current === true) {
                onChange === null || onChange === void 0 ? void 0 : onChange(value || false);
            }
        });
    }, [inView, onInView, onChange]);
};
export const LoadMediaInView = (props) => (React.createElement(InView, { ...props, alwaysRenderChildren: true }));
const Positioner = styled.div `
  height: 0 !important;
  width: 0 !important;
  pointer-events: none;
  flex-grow: 0;
`;
